import React, { useEffect } from 'react';
import { privacyNoticeHTML } from './privacy.js?v=2';
import { termsHTML } from './terms.js';
import { cookieHTML } from './cookie.js';
import useTripwireState from './useTripwireState';
import useLegalModal from './useLegalModal';
import LegalModal from './LegalModal';
import { WHITE_LABEL_CONFIG } from './white_label';

const TripwireLanding = () => {
  const {
    formattedCurrentPrice,
    formattedStrikethroughPrice,
    ctaText,
    paymentLink,
  } = useTripwireState();
  const { modalOpen, modalType, openModal, closeModal, openCookieSettings } = useLegalModal();

  useEffect(() => {
    document.title = WHITE_LABEL_CONFIG.META_TITLE;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', WHITE_LABEL_CONFIG.META_DESCRIPTION);
    }
  }, []);

  const getExamCountdownLine = () => {
    const examDateString = WHITE_LABEL_CONFIG.SCHOOL_ENTRANCE_EXAM_DATE;
    const examLabel = WHITE_LABEL_CONFIG.SCHOOL_ENTRANCE_EXAM_LABEL;

    if (!examDateString || !examLabel) {
      return null;
    }

    const examDate = new Date(`${examDateString}T00:00:00`);
    if (Number.isNaN(examDate.getTime())) {
      return null;
    }

    const now = new Date();
    const msRemaining = examDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

    if (daysRemaining <= 0) {
      return null;
    }

    const formattedDate = examDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    let timeText;

    if (daysRemaining < 21) {
      const dayLabel = daysRemaining === 1 ? 'day' : 'days';
      timeText = `Only ${daysRemaining} ${dayLabel} until the ${examLabel}`;
    } else if (daysRemaining <= 70) {
      const weeksRemaining = Math.ceil(daysRemaining / 7);
      const weekLabel = weeksRemaining === 1 ? 'week' : 'weeks';
      timeText = `Only ${weeksRemaining} ${weekLabel} until the ${examLabel}`;
    } else {
      const monthsRemaining = Math.max(1, Math.ceil(daysRemaining / 30.44));
      const monthLabel = monthsRemaining === 1 ? 'month' : 'months';
      timeText = `${monthsRemaining} ${monthLabel} until the ${examLabel}`;
    }

    return {
      fullLine: `Next ${examLabel}: ${formattedDate} - ${timeText}`,
      prefix: `Next ${examLabel}:`,
      formattedDate,
      timeText,
    };
  };

  const examCountdownLine = getExamCountdownLine();

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-gray-900 pb-24 md:pb-0">
      {examCountdownLine && (
        <div className="relative z-20 border-b border-gold-400/20 bg-gradient-to-r from-navy-900 via-navy-800 to-navy-900 shadow-[0_8px_24px_rgba(15,23,42,0.18)]">
          <div className="max-w-5xl mx-auto px-4 py-3 text-center text-[0.82rem] sm:text-sm text-white/90 tracking-[0.01em]">
            {examCountdownLine.fullLine}
          </div>
        </div>
      )}
      {/* Hero Section */}
      <section className="relative px-4 py-10 md:py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&q=80" 
            alt="Student focusing on exam" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-800 to-navy-900 opacity-90"></div>
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
            <div className="lg:w-1/2">
              <img
                src={WHITE_LABEL_CONFIG.BRAND_LOGO_LIGHT_SRC}
                alt={`${WHITE_LABEL_CONFIG.BRAND_NAME} logo`}
                className="h-6 sm:h-7 w-auto mb-5 sm:mb-6"
              />
              <p className="inline-flex items-center rounded-full border border-gold-400/40 bg-white/10 px-4 py-2 text-xs sm:text-sm font-medium text-gold-100 backdrop-blur-sm mb-3 sm:mb-4 max-w-full">
                {WHITE_LABEL_CONFIG.SCHOOL_DIAGNOSTIC_LABEL} with professional marking
              </p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem] font-serif font-bold leading-[1.05] mb-4 sm:mb-5 text-white max-w-xl text-balance">
                Know exactly where your child stands before the <span className="text-gold-500">{WHITE_LABEL_CONFIG.SCHOOL_NAME}</span> exam
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-5 sm:mb-7 max-w-xl">
                Run a timed mock at home, get it professionally marked, and receive a personalised gap analysis showing exactly what to improve next.
              </p>
              <ul className="mb-5 sm:mb-7 max-w-xl space-y-1.5 sm:space-y-2 text-sm sm:text-base md:text-lg text-gray-100">
                <li className="flex items-center gap-3"><span className="text-gold-400">•</span><span><strong>Timed Bolton-style mock</strong></span></li>
                <li className="flex items-center gap-3"><span className="text-gold-400">•</span><span><strong>Professional marking</strong></span></li>
                <li className="flex items-center gap-3"><span className="text-gold-400">•</span><span><strong>Personalised next-step report</strong></span></li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-4 sm:items-stretch max-w-xl">
                <a 
                  href="#pay"
                  className="w-full sm:w-auto bg-gold-500 text-navy-900 border-2 border-gold-500 font-bold py-3.5 sm:py-4 px-6 sm:px-8 rounded-full text-base sm:text-lg hover:bg-gold-600 hover:border-gold-600 hover:scale-[1.02] hover:shadow-2xl transition-all shadow-xl focus:outline-none focus:ring-2 focus:ring-gold-300 focus:ring-offset-2 text-center"
                >
                  {ctaText}
                </a>
                <a 
                  href="#report" 
                  className="w-full sm:w-auto border-2 border-white/40 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 font-bold py-3.5 sm:py-4 px-6 sm:px-8 rounded-full text-base sm:text-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gold-300 focus:ring-offset-2 text-center"
                >
                  View Sample Report
                </a>
              </div>
              <p className="mt-3 sm:mt-4 text-sm sm:text-[0.95rem] text-gray-300 max-w-md leading-relaxed">
                Without a proper diagnostic, it&rsquo;s easy to spend months revising the wrong things.
              </p>
              <p className="mt-2 text-sm sm:text-[0.95rem] text-gray-300 max-w-md leading-relaxed">
                Includes a timed mock PDF and personalised digital report
              </p>
            </div>
            <div className="lg:w-1/2 lg:pt-2">
              <div>
                <img 
                  src={WHITE_LABEL_CONFIG.SCHOOL_IMAGE_SRC}
                  alt={WHITE_LABEL_CONFIG.SCHOOL_IMAGE_ALT}
                  className="w-full aspect-[4/3] sm:aspect-[16/10] rounded-2xl shadow-2xl border-4 border-gold-500 object-cover"
                />
                {examCountdownLine && (
                  <p className="mt-4 text-[0.85rem] sm:text-sm text-gray-200 leading-relaxed lg:max-w-md">
                    {examCountdownLine.prefix} <strong>{examCountdownLine.formattedDate}</strong> - {examCountdownLine.timeText}
                  </p>
                )}
              </div>
           </div>
        </div>
        </div>
      </section>

      <section className="px-4 py-8 md:py-10 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            <div className="rounded-2xl border border-gray-200 bg-slate-50 p-4 sm:p-5 shadow-sm">
              <p className="text-xs sm:text-sm uppercase tracking-[0.18em] text-gray-500 mb-2">Clarity</p>
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-navy-900 mb-2">Know their real starting point</h3>
                <p className="text-gray-700">See performance under timed conditions.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-slate-50 p-4 sm:p-5 shadow-sm">
              <p className="text-xs sm:text-sm uppercase tracking-[0.18em] text-gray-500 mb-2">Focus</p>
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-navy-900 mb-2">See exactly what is costing marks</h3>
                <p className="text-gray-700">Spot weak areas and lost-mark patterns.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-slate-50 p-4 sm:p-5 shadow-sm">
              <p className="text-xs sm:text-sm uppercase tracking-[0.18em] text-gray-500 mb-2">Confidence</p>
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-navy-900 mb-2">Know what to fix next</h3>
                <p className="text-gray-700">Focus revision where it matters most.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Stack */}
        <section id="value" className="px-4 py-14 md:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-8 sm:mb-12">
              Here&rsquo;s what you actually receive
            </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8">
            {/* Column 1 */}
             <div className="bg-gradient-to-br from-navy-50 to-slate-50 p-5 sm:p-8 rounded-2xl shadow-xl border border-gray-200">
              <div className="text-navy-900 mb-6">
                <div className="w-16 h-16 bg-gold-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold">1</span>
                </div>
                <h3 className="text-2xl font-serif font-bold mb-4 text-center">Timed Mock PDF</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-gold-500 mr-2">✓</span>
                  <span><strong>Bolton-style timed paper</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-2">✓</span>
                  <span><strong>Run at home</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-2">✓</span>
                  <span><strong>Reveals real weak spots</strong></span>
                </li>
              </ul>
              <p className="mt-4 text-sm text-gray-600">A more realistic benchmark than casual practice.</p>
            </div>

            {/* Column 2 */}
             <div className="bg-gradient-to-br from-navy-50 to-slate-50 p-5 sm:p-8 rounded-2xl shadow-xl border border-gray-200">
              <div className="text-navy-900 mb-6">
                <div className="w-16 h-16 bg-gold-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold">2</span>
                </div>
                <h3 className="text-2xl font-serif font-bold mb-4 text-center">Professional Marking Report</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-gold-500 mr-2">✓</span>
                  <span><strong>Human-marked answers</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-2">✓</span>
                  <span><strong>Clear topic breakdown</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-2">✓</span>
                  <span><strong>Shows where marks go</strong></span>
                </li>
              </ul>
              <p className="mt-4 text-sm text-gray-600">See where marks are being lost, not just the final score.</p>
            </div>

            {/* Column 3 */}
             <div className="bg-gradient-to-br from-navy-50 to-slate-50 p-5 sm:p-8 rounded-2xl shadow-xl border border-gray-200">
              <div className="text-navy-900 mb-6">
                <div className="w-16 h-16 bg-gold-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold">3</span>
                </div>
                <h3 className="text-2xl font-serif font-bold mb-4 text-center">Personalised Digital Gap Analysis</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-gold-500 mr-2">✓</span>
                  <span><strong>Priority gaps identified</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-2">✓</span>
                  <span><strong>Next-step guidance</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-2">✓</span>
                  <span><strong>Helps focus revision</strong></span>
                </li>
              </ul>
              <p className="mt-4 text-sm text-gray-600">Clearer direction on what to improve next.</p>
              
            </div>
          </div>

        </div>
      </section>

      {/* Benchmarking Section */}
      <section className="px-4 py-14 md:py-24 bg-gradient-to-b from-white to-navy-50">
        <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-8 sm:mb-12">
              Practice papers show scores. We show what the score means.
            </h2>
          <div className="bg-white p-5 sm:p-8 md:p-12 rounded-2xl shadow-2xl border-t-4 border-gold-500">
             <p className="text-lg sm:text-xl md:text-2xl text-gray-800 mb-3 sm:mb-4 text-center">
               GL-style materials can help your child practise, but a score on its own does not tell you where the real gaps are or what to do next. This diagnostic turns performance into clear next-step guidance for parents.
             </p>
             <p className="text-center text-xl sm:text-2xl font-bold text-navy-900 mt-6 sm:mt-8 mb-7 sm:mb-10">The value is not just the mock. It is knowing what to do next.</p>
             
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 mb-8 sm:mb-10">
              {/* GL resources card */}
               <div className="bg-gradient-to-br from-navy-50 to-slate-50 p-5 sm:p-6 rounded-xl border-2 border-navy-200">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-navy-500 text-white rounded-full flex items-center justify-center mr-4">
                    <span className="text-xl font-bold">GL</span>
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-navy-900">Practice materials</h3>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start"><span className="text-gold-500 mr-3">•</span><span>good for exposure to question types</span></li>
                  <li className="flex items-start"><span className="text-gold-500 mr-3">•</span><span>useful for familiarisation</span></li>
                  <li className="flex items-start"><span className="text-gold-500 mr-3">•</span><span>do not explain why marks are being lost</span></li>
                  <li className="flex items-start"><span className="text-gold-500 mr-3">•</span><span>do not tell parents what to prioritise next</span></li>
                </ul>
              </div>

              {/* School-specific card */}
              <div className="bg-gradient-to-br from-navy-50 to-slate-50 p-5 sm:p-6 rounded-xl border-2 border-navy-200">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-navy-500 text-white rounded-full flex items-center justify-center mr-4">
                    <span className="text-xl font-bold">{WHITE_LABEL_CONFIG.SCHOOL_MONOGRAM}</span>
                  </div>
                  <h3 className="text-2xl font-serif font-bold text-navy-900">EntryMetric diagnostic</h3>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start"><span className="text-gold-500 mr-3">•</span><span>shows how your child performs under timed conditions</span></li>
                  <li className="flex items-start"><span className="text-gold-500 mr-3">•</span><span>identifies weak spots and missed patterns</span></li>
                  <li className="flex items-start"><span className="text-gold-500 mr-3">•</span><span>gives parent-friendly interpretation</span></li>
                  <li className="flex items-start"><span className="text-gold-500 mr-3">•</span><span>helps you focus revision more effectively</span></li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section id="report" className="px-4 py-14 md:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-navy-900 mb-4 sm:mb-6">
            What the report actually helps you see
          </h2>
          <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto mb-7 sm:mb-10">
            A score alone doesn&rsquo;t tell you why marks are being lost. This report does. It helps you see which gaps matter most, where errors are happening, and what to focus on next.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-6 items-center">
            <div className="flex h-full flex-col justify-center py-1">
              <img
                src="/images/bolton-school-pack.jpg"
                alt={`Example diagnostic report for ${WHITE_LABEL_CONFIG.SCHOOL_NAME}`}
                className="w-full rounded-2xl border border-slate-200/80 bg-white object-contain shadow-[0_18px_45px_-28px_rgba(15,23,42,0.28)]"
              />
              <p className="mt-4 max-w-xl text-base sm:text-lg leading-relaxed text-navy-800">
                A complete digital diagnostic pack —  including a timed mock, professional marking, and a personalised report showing exactly what to improve next.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-3xl p-4 sm:p-8 shadow-xl">
              <h3 className="text-2xl font-serif font-bold text-navy-900 mb-4 sm:mb-5">Your timed digital pack includes:</h3>
              <ul className="space-y-3 sm:space-y-4 text-gray-700">
                <li className="flex items-start"><span className="text-gold-500 mr-3">✓</span><span>a realistic timed mock</span></li>
                <li className="flex items-start"><span className="text-gold-500 mr-3">✓</span><span>professional marking</span></li>
                <li className="flex items-start"><span className="text-gold-500 mr-3">✓</span><span>a clear breakdown of weaker areas</span></li>
                <li className="flex items-start"><span className="text-gold-500 mr-3">✓</span><span>practical next-step guidance for parents</span></li>
              </ul>
              <div className="mt-5 sm:mt-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-2">Example insight</p>
                <p className="text-sm sm:text-base text-gray-800">Strong overall score, but timing pressure and non-verbal pattern recognition are holding performance back.</p>
              </div>
              <div className="mt-6 sm:mt-8 rounded-2xl bg-slate-50 border border-gray-200 p-4 sm:p-5">
                <h4 className="text-lg font-bold text-navy-900 mb-2.5 sm:mb-3">Why parents find this useful</h4>
                <p className="text-gray-700">Timed conditions often reveal hesitation, weak reasoning patterns, and avoidable mistakes that normal practice misses.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Section */}
       <section className="px-4 py-12 md:py-16 bg-gradient-to-b from-gray-100 to-slate-50">
         <div className="max-w-5xl mx-auto">
             <h2 className="text-3xl md:text-4xl font-serif font-bold text-center mb-4 md:mb-5">
              Why generic 11+ prep falls short
            </h2>
            <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto mb-7 md:mb-8">
              Generic practice can give exposure, but it does not show which weaknesses matter most or what to focus on next.
            </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-stretch mb-8 md:mb-9">
            <div className="rounded-3xl border-2 border-red-200 bg-white p-7 sm:p-8 md:p-10 shadow-lg min-h-[260px] flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600 text-2xl shadow-sm">
                  <span>!</span>
                </div>
                <h3 className="text-2xl md:text-[1.7rem] font-serif font-bold text-navy-900">Generic prep often gives you:</h3>
              </div>
              <ul className="space-y-3.5 text-gray-800 text-base sm:text-lg">
                <li className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50/70 px-4 py-3.5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-red-500 shadow-sm">-</span>
                  <span><strong>More papers</strong>, but no diagnosis</span>
                </li>
                <li className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50/70 px-4 py-3.5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-red-500 shadow-sm">-</span>
                  <span><strong>More questions</strong>, but no priority order</span>
                </li>
                <li className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50/70 px-4 py-3.5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-red-500 shadow-sm">-</span>
                  <span><strong>More effort</strong>, but no clarity</span>
                </li>
              </ul>
            </div>
            <div className="rounded-3xl border-2 border-green-200 bg-white p-7 sm:p-8 md:p-10 shadow-lg min-h-[260px] flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700 text-2xl shadow-sm">
                  <span>+</span>
                </div>
                <h3 className="text-2xl md:text-[1.7rem] font-serif font-bold text-navy-900">This diagnostic gives you:</h3>
              </div>
              <ul className="space-y-3.5 text-gray-800 text-base sm:text-lg">
                <li className="flex items-start gap-3 rounded-2xl border border-green-100 bg-green-50/70 px-4 py-3.5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-green-600 shadow-sm">+</span>
                  <span><strong>Clear weak spots</strong> to focus on first</span>
                </li>
                <li className="flex items-start gap-3 rounded-2xl border border-green-100 bg-green-50/70 px-4 py-3.5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-green-600 shadow-sm">+</span>
                  <span><strong>A focused next-step plan</strong> for revision</span>
                </li>
                <li className="flex items-start gap-3 rounded-2xl border border-green-100 bg-green-50/70 px-4 py-3.5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-green-600 shadow-sm">+</span>
                  <span><strong>Better use of revision time</strong> before the exam</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="rounded-3xl border border-slate-300 bg-slate-50 p-6 sm:p-8 md:p-9 shadow-md">
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-4 md:mb-5">What this helps you uncover</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 text-gray-950">
              <li className="rounded-2xl bg-white border border-slate-300 px-6 py-5 sm:py-6 shadow-sm min-h-[96px] flex items-center text-[1.03rem] sm:text-lg leading-relaxed"><span><strong>Weak timing</strong> under pressure</span></li>
              <li className="rounded-2xl bg-white border border-slate-300 px-6 py-5 sm:py-6 shadow-sm min-h-[96px] flex items-center text-[1.03rem] sm:text-lg leading-relaxed"><span><strong>Avoidable reasoning</strong> mistakes</span></li>
              <li className="rounded-2xl bg-white border border-slate-300 px-6 py-5 sm:py-6 shadow-sm min-h-[96px] flex items-center text-[1.03rem] sm:text-lg leading-relaxed"><span><strong>Gaps hidden</strong> by untimed practice</span></li>
              <li className="rounded-2xl bg-white border border-slate-300 px-6 py-5 sm:py-6 shadow-sm min-h-[96px] flex items-center text-[1.03rem] sm:text-lg leading-relaxed"><span><strong>Overconfidence</strong> in certain topics</span></li>
              <li className="rounded-2xl bg-white border border-slate-300 px-6 py-5 sm:py-6 shadow-sm min-h-[96px] flex items-center sm:col-span-2 text-[1.03rem] sm:text-lg leading-relaxed"><span><strong>Where tutoring</strong> would help most</span></li>
            </ul>
          </div>
        </div>
      </section>

      <section className="px-4 py-8 md:py-9 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-5 sm:gap-6 items-center">
              <div className="lg:order-2">
                <img
                  src="/images/entry-metric-pack.png"
                  alt="Diagnostic pack preview"
                  className="w-full h-auto rounded-xl"
                  loading="lazy"
                />
              </div>

              <div className="lg:order-1">
                <h2 className="text-2xl md:text-[1.9rem] font-serif font-bold text-navy-900 mb-3 sm:mb-4">This diagnostic is ideal if:</h2>
                <ul className="space-y-2.5 sm:space-y-3 text-gray-800 mb-3 sm:mb-4">
                  <li className="flex items-start gap-3 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3"><span className="text-gold-600 mt-0.5">✓</span><span><strong>your child is preparing for Bolton School</strong></span></li>
                  <li className="flex items-start gap-3 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3"><span className="text-gold-600 mt-0.5">✓</span><span><strong>you want a realistic at-home benchmark</strong></span></li>
                  <li className="flex items-start gap-3 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3"><span className="text-gold-600 mt-0.5">✓</span><span><strong>you are unsure what to focus on next</strong></span></li>
                  <li className="flex items-start gap-3 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3"><span className="text-gold-600 mt-0.5">✓</span><span><strong>you want clarity before buying more tutoring or more papers</strong></span></li>
                </ul>
                <p className="text-sm sm:text-[0.95rem] text-gray-700 leading-relaxed">This is a diagnostic, not a full tuition programme. Its purpose is to show where your child stands and what to improve next.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pay" className="px-4 py-10 md:py-14 bg-white scroll-mt-24">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-gold-50 to-white border-2 border-gold-300 rounded-2xl p-5 sm:p-8 md:p-10 shadow-xl">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 text-center mb-4">
              Get your {WHITE_LABEL_CONFIG.SCHOOL_NAME} diagnostic pack
            </h2>
            <p className="text-center text-lg text-gray-700 mb-6 sm:mb-8">
              One payment. No subscription. Clear next steps for parents.
            </p>
            <div className="flex flex-col md:flex-row md:items-center gap-5 sm:gap-6 md:gap-8 bg-white rounded-xl border border-gray-200 px-4 sm:px-6 py-4 sm:py-5 md:py-6">
              <div className="text-center md:text-left md:flex-1 md:max-w-none">
                <p className="text-sm uppercase tracking-wide text-gray-500">Today&rsquo;s price</p>
                <p className="text-4xl font-bold text-navy-900">
                  {formattedCurrentPrice} {formattedStrikethroughPrice && <span className="text-2xl line-through text-gray-400 ml-2">{formattedStrikethroughPrice}</span>}
                </p>
                <ul className="mt-4 space-y-2.5 text-sm sm:text-base text-gray-800">
                  <li className="border-b border-gray-100 pb-2"><strong>Timed mock paper</strong></li>
                  <li className="border-b border-gray-100 pb-2"><strong>Professional marking</strong></li>
                  <li className="border-b border-gray-100 pb-2"><strong>Personalised gap analysis</strong></li>
                  <li><strong>Parent-friendly next-step guidance</strong></li>
                </ul>
                <p className="mt-3 text-xs sm:text-sm text-gray-600 leading-relaxed">Digital pack includes timed mock PDF, professional marking, and personalised digital gap analysis.</p>
                <p className="text-sm font-medium text-gray-700 mt-4">One payment. No subscription.</p>
              </div>
              <a
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full md:w-auto md:self-center text-center bg-gold-500 text-navy-900 border-2 border-gold-500 font-bold py-4 px-7 sm:px-10 rounded-full text-base sm:text-lg hover:bg-gold-600 hover:border-gold-600 hover:scale-[1.02] hover:shadow-2xl transition-all shadow-xl focus:outline-none focus:ring-2 focus:ring-gold-300 focus:ring-offset-2"
              >
                Continue to secure checkout ->
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA & Risk Reversal */}
      <section className="px-4 py-14 md:py-24 bg-gradient-to-r from-navy-900 to-navy-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
            <img
              src={WHITE_LABEL_CONFIG.BRAND_LOGO_LIGHT_SRC}
              alt={`${WHITE_LABEL_CONFIG.BRAND_NAME} logo`}
              className="h-5 sm:h-6 w-auto mx-auto mb-6 opacity-95"
            />
            <h2 className="text-3xl md:text-4xl font-bold mb-5 md:mb-8">
             Find the gaps now &mdash; while there&rsquo;s still time to fix them
            </h2>
          <p className="text-lg md:text-xl mb-6 sm:mb-8 md:mb-10">
            Get a realistic Bolton-style diagnostic, professional marking, and a parent-friendly report so you know what to focus on next.
          </p>
          
          <div className="mb-9 sm:mb-12">
            <a 
              href="#pay"
              className="inline-block w-full sm:w-auto bg-gold-500 text-navy-900 border-2 border-gold-500 font-bold py-3.5 sm:py-4 px-6 sm:px-8 rounded-full text-base sm:text-lg hover:bg-gold-600 hover:border-gold-600 hover:scale-[1.02] hover:shadow-2xl transition-all shadow-xl focus:outline-none focus:ring-2 focus:ring-gold-300 focus:ring-offset-2"
            >
               {ctaText}
              </a>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-5 sm:p-8 rounded-2xl border-2 border-gold-500">
            <h3 className="text-2xl font-bold mb-4">100% Satisfaction Guarantee</h3>
            <p className="text-lg md:text-xl">
               If the diagnostic doesn&rsquo;t give you clear, useful next steps, email us within 7 days and we&rsquo;ll make it right.
            </p>
            <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-center gap-3.5 sm:gap-6">
              <div className="flex items-center w-full sm:w-auto justify-start text-left">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                  <span className="text-2xl">✓</span>
                </div>
                <span className="text-lg">Useful feedback</span>
              </div>
              <div className="flex items-center w-full sm:w-auto justify-start text-left">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                  <span className="text-2xl">✓</span>
                </div>
                <span className="text-lg">Human-reviewed</span>
              </div>
              <div className="flex items-center w-full sm:w-auto justify-start text-left">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                  <span className="text-2xl">✓</span>
                </div>
                <span className="text-lg">No subscription</span>
              </div>
            </div>
          </div>

          <div className="mt-8 sm:mt-10 bg-white/5 border border-white/20 rounded-2xl p-5 md:p-8 text-left">
            <h3 className="text-2xl font-bold mb-4 sm:mb-5 text-center">Questions parents ask before buying</h3>
            <div className="space-y-7 sm:space-y-7 text-gray-100">
              <div className="space-y-2">
                <p className="font-semibold">How is this different from buying practice papers?</p>
                <p className="text-gray-200 leading-7 sm:leading-relaxed">Practice papers can show how many questions your child gets right. This diagnostic goes further by showing where marks are being lost, which patterns matter most, and what to focus on next.</p>
              </div>
              <div className="space-y-2">
                <p className="font-semibold">Is this official Bolton School material?</p>
                <p className="text-gray-200 leading-7 sm:leading-relaxed">No. This is an independent diagnostic designed to help parents benchmark performance and identify gaps before the real exam.</p>
              </div>
              <div className="space-y-2">
                <p className="font-semibold">What happens after I buy?</p>
                <p className="text-gray-200 leading-7 sm:leading-relaxed">You receive a timed mock PDF, complete it at home, and send back your answers for review. We then return professionally marked feedback and a personalised digital gap analysis.</p>
              </div>
              <div className="space-y-2">
                <p className="font-semibold">Who is this best suited for?</p>
                <p className="text-gray-200 leading-7 sm:leading-relaxed">It is best for parents who want a clearer picture of readiness before spending more time or money on broad, unfocused preparation.</p>
              </div>
            </div>
          </div>

           <div className="mt-8 flex justify-center gap-x-4 md:gap-x-8 gap-y-2 flex-wrap">
             <a 
               href="#terms"
               onClick={(e) => {
                 e.preventDefault();
                 openModal('terms');
               }}
               className="text-gold-500 hover:text-gold-400 font-medium transition"
             >
               Terms of Service
             </a>
             <a 
               href="#privacy"
               onClick={(e) => {
                 e.preventDefault();
                 openModal('privacy');
               }}
               className="text-gold-500 hover:text-gold-400 font-medium transition"
             >
               Privacy Policy
             </a>
             <a 
               href="#cookies"
               onClick={(e) => {
                 e.preventDefault();
                 openModal('cookie');
               }}
               className="text-gold-500 hover:text-gold-400 font-medium transition"
             >
               Cookie Policy
             </a>
            <button 
              onClick={openCookieSettings}
              className="text-gold-500 hover:text-gold-400 font-medium transition"
            >
              Cookie Settings
            </button>
          </div>
          <p className="mt-8 text-gray-300">
            © {new Date().getFullYear()} {WHITE_LABEL_CONFIG.BRAND_NAME} | {WHITE_LABEL_CONFIG.FOOTER_TAGLINE}
          </p>
          <p className="mt-0 text-gray-300">
            {WHITE_LABEL_CONFIG.BRAND_LEGAL_NAME} is a provider of independent academic benchmarking.
          </p>
        </div>
      </section>

      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur border-t border-navy-200 shadow-[0_-10px_30px_rgba(15,23,42,0.12)] p-3">
        <a
          href="#pay"
          className="block w-full text-center bg-gold-500 text-navy-900 font-bold py-3.5 rounded-full text-sm sm:text-base shadow-[0_8px_18px_rgba(184,134,11,0.22)] border border-gold-600 transition-all duration-200 hover:bg-gold-600 hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(184,134,11,0.28)] active:translate-y-0"
        >
          Get My Child&rsquo;s Gap Analysis - {formattedCurrentPrice}
        </a>
      </div>

      <LegalModal
        modalOpen={modalOpen}
        modalType={modalType}
        closeModal={closeModal}
        termsHTML={termsHTML}
        privacyNoticeHTML={privacyNoticeHTML}
        cookieHTML={cookieHTML}
        overlayClassName="bg-gray-900 bg-opacity-75"
        contentClassName="privacy-content prose text-gray-700 overflow-y-auto max-h-[60vh]"
      />
    </div>
  );
};

export default TripwireLanding;
